import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from '@/auth/AuthContext';
import { Box, Flex, SimpleGrid, Skeleton, Stack, Text, VStack } from "@chakra-ui/react";
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext } from "src/components/DataTable";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { LuHandshake, LuScale, LuWalletCards } from "react-icons/lu";
import type { IconType } from "react-icons";
import { ProjectYear } from "src/api/ProjectYear";
import Content from "src/components/Content"
import DataTable, { DataTableToolbar, amountHeader, dateCell, moneyCell, moneyListCell, moneySort, moneyText, amountCell, valueCurrencyText } from "src/components/DataTable";
import { useProjectNames } from "@/hooks/useProjectNames";
import { reportService } from "src/service/ReportService";
import {
    Select as ReactSelect
} from "chakra-react-select";
import { Money } from "src/api/Money";
import { ProgressBarReact } from "src/components/ProgressBar";
import { TransferYear } from "src/api/TransferYear";
import { TransferDifference } from "src/api/TransferDifference";
import { MoneyUtil } from "src/data/MoneyUtil";
import { ProjectBalance } from "src/api/ProjectBalance";
import { ProjectBalanceReportResponse } from "src/api/ProjectBalanceReportResponse";
import WaitingSpinner from "@/components/WaitingSpinner";
import { StatRoot, StatDownTrend, StatUpTrend } from "@/components/ui/stat"
import StatCard from "@/components/StatCard";
import { useI18n } from '@/i18n/I18nContext';

type ProjectStat = {
    key: string;
    label: string;
    value: string;
    helpText: string;
    icon: IconType;
    accentColor: string;
    iconBg: string;
    valueColor?: string;
};

const DashboardProject = () => {
    const { t } = useI18n();
    const organization = useOrganization();
    const homeCurrency = organization?.currency;
    const { id } = useParams();
    // const currentYear = new Date().getFullYear();

    const [projectId, setProjectId] = useState<number | null>(id ? parseInt(id) : null);
    const { options: projectNames } = useProjectNames();
    const [data, setData] = useState<ProjectYear[]>([]);
    const [difference, setDifference] = useState<TransferDifference[]>([]);
    const [differenceTotal, setDifferenceTotal] = useState<number>(0);
    const [plannings, setPlannings] = useState<Map<number, TransferYear>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [balance, setBalance] = useState<ProjectBalanceReportResponse | null>(null);

    const renderSummaryCards = () => {
        const partnerStats: ProjectStat[] = balance
            ? balance.partner.map((pm, index) => ({
                key: `partner-${index}`,
                label: 'Fond beim Partner',
                value: MoneyUtil.format(pm),
                helpText: 'Wie viel Geld hat der Partner noch',
                icon: LuHandshake,
                accentColor: 'teal.600',
                iconBg: 'teal.50',
            }))
            : [{
                key: 'partner',
                label: 'Fond beim Partner',
                value: MoneyUtil.format(undefined),
                helpText: 'Wie viel Geld hat der Partner noch',
                icon: LuHandshake,
                accentColor: 'teal.600',
                iconBg: 'teal.50',
            }];

        const stats: ProjectStat[] = [
            {
                key: 'provision',
                label: 'Projektfonds',
                value: MoneyUtil.format(balance?.provision),
                helpText: 'Wie viel Geld steht aktuell für das Projekt',
                icon: LuWalletCards,
                accentColor: 'blue.600',
                iconBg: 'blue.50',
            },
            ...partnerStats,
            // {
            //     key: 'borrowed',
            //     label: 'Ausgeliehen',
            //     value: MoneyUtil.format(balance?.borrowed),
            //     helpText: 'Wie viel Geld wurde ausgeliehen von den Internen Reserven',
            //     icon: LuBanknote,
            //     accentColor: 'orange.600',
            //     iconBg: 'orange.50',
            // },
            {
                key: 'difference',
                label: 'WK Differenz',
                value: `${differenceTotal > 0 ? '+' : ''}${MoneyUtil.format({ value: differenceTotal, currency: difference[0]?.transfer.currency ?? homeCurrency })}`,
                helpText: 'Differenz zwischen der Überweisung mit dem tatsächlichen und dem budgetierten Wechselkurs',
                icon: LuScale,
                accentColor: differenceTotal < 0 ? 'red.600' : differenceTotal > 0 ? 'green.600' : 'gray.600',
                iconBg: differenceTotal < 0 ? 'red.50' : differenceTotal > 0 ? 'green.50' : 'gray.100',
                valueColor: differenceTotal < 0 ? 'red.600' : differenceTotal > 0 ? 'green.600' : undefined,
            },
            // {
            //     key: 'commitment',
            //     label: 'Zusagen',
            //     value: MoneyUtil.format(balance?.commitmentThisYear),
            //     helpText: `Zusagen aus dem aktuellen Jahr ${currentYear}`,
            //     icon: LuArrowLeftRight,
            //     accentColor: 'purple.600',
            //     iconBg: 'purple.50',
            // },
        ];

        return (
            <SimpleGrid columns={{ base: 1, md: 2, xl: Math.min(stats.length, 4) }} gap='4' w='full'>
                {stats.map((stat) => (
                    <StatCard
                        key={stat.key}
                        label={stat.label}
                        value={stat.value}
                        helpText={stat.helpText}
                        icon={stat.icon}
                        accentColor={stat.accentColor}
                        iconBg={stat.iconBg}
                        valueColor={stat.valueColor}
                        valueAlign='end'
                        valueFontSize='xl'
                        isLoading={isLoading}
                    />
                ))}
            </SimpleGrid>
        );
    };

    const loadProjectData = (projectId: number): Promise<void> => {
        setIsLoading(true);

        const a = reportService.projectDetail(projectId)
            .then(r => {
                let total: ProjectYear = {
                    key: {
                        projectId: projectId,
                        year: undefined
                    },
                    balance: r.overall.balance,
                    coverage: r.overall.coverage
                }
                // overall aggregations with every year
                setData(r.results.concat(total));
            });
        const b = reportService.projectTransfer(projectId)
            .then(r => {
                setDifference(r.differences.flatMap(d => d.transfers));
                setPlannings(new Map(r.differences.map(d => [d.year, d])));
                // calculate the total of difference from the conversion rates
                setDifferenceTotal(r.differences
                    .flatMap(d => d.transfers)
                    .map(t => t.plannedTransfer.value - t.transfer.value)
                    .reduce((d1, d2) => d1 + d2, 0));
            });
        const c = reportService.projectBalance(projectId)
            .then(r => setBalance(r))

        return Promise.all([a, b, c]).then(_ => { }).finally(() => setIsLoading(false));
    }

    useEffect(() => {
        if (projectId) {
            loadProjectData(projectId);
        }
    }, [projectId])

    const moneyCell2 = (width: string = '10rem') => (info: CellContext<ProjectYear, Money>) => {
        const money = info.getValue();
        return moneyText(money, width, info.row.original.key.year ? 'normal' : 'extrabold');
    }

    const columns: ColumnDef<ProjectYear>[] = useMemo(
        () => [
            {
                id: 'year',
                header: 'Mitteljahr',
                accessorFn: (row: ProjectYear) => row.key.year,
                cell: (info: CellContext<ProjectYear, number>) => {
                    let year = info.getValue();
                    return (<Text width='3rem' fontWeight='bold'>{year ?? 'Total'}</Text>)
                },
            },
            {
                id: 'budget',
                header: amountHeader({ name: ['Projekt Budget', '(BUD WK)'] }),
                accessorKey: 'balance.budget',
                cell: moneyCell2(),
                sortFn: moneySort(),
            },
            {
                id: 'transfer',
                header: amountHeader({ name: ['Transfer', '(Aktueller WK)'], explanation: 'Transfers an Partner und Konsulenten (in grün)' }),
                accessorFn: (row: ProjectYear) => row.balance,
                cell: (info: CellContext<ProjectYear, ProjectBalance>) => {
                    let balance = info.getValue();
                    let fontWeight = info.row.original.key.year ? 'normal' : 'extrabold';
                    return <VStack>
                        {moneyText(balance.transfer, '10rem', fontWeight)}
                        {balance.transferConsultant.value !== 0 && <Flex color='green' fontSize='small'>{moneyText(balance.transferConsultant, '10rem', fontWeight)}</Flex>}
                    </VStack>
                }
            },
            {
                id: 'localBudget',
                header: amountHeader({ name: 'Lokal Budget' }),
                accessorKey: 'balance.localBudget',
                cell: moneyCell2('12rem'),
                sortFn: moneySort(),
            },
            {
                id: 'localTransfers',
                header: amountHeader({ name: 'Lokal Transfer', explanation: 'Transfers an Partner und Konsulenten (in grün) in Fremdwährungen' }),
                accessorFn: (row: ProjectYear) => row.balance,
                cell: (info: CellContext<ProjectYear, ProjectBalance>) => {
                    let balance = info.getValue();
                    let fontWeight = info.row.original.key.year ? 'normal' : 'extrabold';
                    return <VStack>
                        {moneyListCell(balance.localTransfers, info.row.original.balance.localBudget.currency, fontWeight, '12rem')}
                        {balance.localTransfersConsultant.length > 0 && <Flex color='green' fontSize='small'>{moneyListCell(balance.localTransfersConsultant, info.row.original.balance.localBudget.currency, fontWeight, '12rem')}</Flex>}
                    </VStack>
                }
            },
            {
                id: 'transferCoverage',
                header: amountHeader({ name: 'Lokal Deckung %', explanation: 'Total Lokal Transfers / Total Lokal Budget' }),
                accessorKey: 'coverage.localTransferCoverage',
                cell: (info: CellContext<ProjectYear, number>) => ProgressBarReact(info.getValue())
            },
            {
                id: 'localExpenses',
                header: amountHeader({ name: 'Lokal Ausgaben', explanation: 'Total Lokal Ausgaben / Total Lokal Budget' }),
                accessorKey: 'balance.localExpenses',
                accessorFn: (row: ProjectYear) => row.balance.localExpenses,
                cell: (info: CellContext<ProjectYear, Money[]>) => moneyListCell(info.getValue(), info.row.original.balance.localBudget.currency, info.row.original.key.year ? 'normal' : 'extrabold', '12rem'),
            },
            {
                id: 'expenseCoverage',
                header: amountHeader({ name: 'Ausgaben %' }),
                accessorKey: 'coverage.expenseCoverage',
                cell: (info: CellContext<ProjectYear, number>) => ProgressBarReact(info.getValue())
            },
        ], []);

    const differenceColumns: ColumnDef<TransferDifference>[] = useMemo(
        () => [
            {
                id: 'year',
                header: 'Mitteljahr',
                accessorFn: (row: TransferDifference) => row.budgetYear,
            },
            {
                id: 'transferDate',
                header: 'Valuta',
                accessorKey: 'transferDate',
                cell: dateCell(),
                enableGrouping: false,
            },
            {
                id: 'budgetRate',
                header: amountHeader({ name: 'Budget Kurs' }),
                accessorFn: row => plannings.get(row.budgetYear)?.plannedRate,
                cell: amountCell({}),
                aggregatedCell: '',
                enableGrouping: false,
            },
            {
                id: 'localTransfer',
                header: amountHeader({ name: 'Lokal Transfer', explanation: 'Transfer in der lokalen oder gewünschten Währung' }),
                accessorKey: 'localTransfer',
                cell: moneyCell(),
                sortFn: moneySort(),
                aggregatedCell: info => {
                    const year = info.row.original.budgetYear;
                    const transfers = plannings.get(year)?.transfers.map(t => t.localTransfer);
                    return MoneyUtil.merge(transfers).map(m => moneyText(m, '10rem', 'normal'));
                },
                enableGrouping: false,
            },
            {
                id: 'plannedTransfer',
                header: amountHeader({ name: ['Transfer', '(BUD WK)'], explanation: 'Wie viel würde die Überweisung mit dem ursprünglichen Budgetkurs' }),
                accessorKey: 'plannedTransfer',
                cell: moneyCell(),
                sortFn: moneySort(),
                aggregatedCell: info => {
                    const year = info.row.original.budgetYear;
                    const sum = MoneyUtil.sumAmount(plannings.get(year)?.transfers.map(t => t.plannedTransfer) ?? []);
                    return valueCurrencyText(sum, plannings.get(year)?.budget.currency ?? homeCurrency, '10rem', 'normal')
                },
                enableGrouping: false,
            },
            {
                id: 'rate',
                header: amountHeader({ name: 'Aktueller Kurs' }),
                accessorKey: 'rate',
                cell: amountCell({}),
                aggregatedCell: '',
                enableGrouping: false,
            },
            {
                id: 'transfer',
                header: amountHeader({ name: 'Transfer Aktueller Kurs', explanation: 'Überweisung mit dem aktuellen Wechselkurs' }),
                accessorKey: 'transfer',
                cell: moneyCell(),
                sortFn: moneySort(),
                aggregatedCell: info => {
                    const year = info.row.original.budgetYear;
                    const sum = MoneyUtil.sumAmount(plannings.get(year)?.transfers.map(t => t.transfer) ?? []);
                    return valueCurrencyText(sum, plannings.get(year)?.budget.currency ?? homeCurrency, '10rem', 'normal')
                },
                enableGrouping: false,
            },
            {
                id: 'difference',
                header: amountHeader({ name: 'Differenz', explanation: 'Differenz zwischen der Überweisung mit dem tatsächlichen und dem budgetierten Wechselkurs' }),
                accessorFn: row => row.plannedTransfer.value - row.transfer.value,
                cell: (info: CellContext<TransferDifference, number>) =>
                    <Flex gap='2px'>
                        {valueCurrencyText(info.getValue(), info.row.original.transfer.currency, '10rem', 'normal', undefined, true)}
                        <Flex>
                            <StatRoot>
                                {info.getValue() < 0 ? <StatDownTrend /> : <StatUpTrend />}
                            </StatRoot>
                        </Flex>
                    </Flex>,
                aggregatedCell: (info: CellContext<TransferDifference, number>) =>
                    <Flex gap='2px'>
                        {valueCurrencyText(info.getValue(), info.row.original.transfer.currency, '10rem', 'normal', undefined, true)}
                        <Flex>
                            <StatRoot>
                                {info.getValue() < 0 ? <StatDownTrend /> : <StatUpTrend />}
                            </StatRoot>
                        </Flex>
                    </Flex>,
                enableGrouping: false,
            },
        ], [plannings, homeCurrency]);

    if (!organization) return <Text role='status'>Organisationseinstellungen werden geladen...</Text>;

    return (
        <Content title={t('projects.report.title')} description={t('projects.report.description')}>
            <Stack gap='6' minW='0'>
                <DataTableToolbar>
                    <Box w={{ base: 'full', md: '20rem' }} minW='0'>
                        <ReactSelect
                            isSearchable
                            required
                            isDisabled={false}
                            autoFocus={false}
                            name='project-select'
                            aria-label={t('projects.details.tab')}
                            placeholder={t('projects.details.tab')}
                            options={projectNames}
                            onChange={val => setProjectId(val?.value ?? null)}
                            value={projectNames.find(c => c.value === projectId) ?? null}
                        />
                    </Box>
                </DataTableToolbar>
                {projectId ? <>
                    {renderSummaryCards()}
                    {isLoading ? (
                        <Stack py={5}>
                            <WaitingSpinner size='xl' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                        </Stack>
                    ) : <>
                        <SectionPanel title={t('projects.report.budgetAndTransfers')}>
                            <DataTable columns={columns} data={data} initialSortByColumn='year' initialSortByDesc={true}
                                hideColumnsOnXs={['transferCoverage', 'expenseCoverage']}
                            />
                        </SectionPanel>
                        <SectionPanel title={t('projects.report.exchangeDifferences')}>
                            <DataTable columns={differenceColumns} data={difference} initialSortByColumn={['year', 'transferDate']} initialSortByDesc={true}
                                initialGroupByColumns={['year']} hideColumnsOnXs={['budget']}
                            />
                        </SectionPanel>
                    </>}
                </> : <Text color='fg.muted' textAlign='center' py='6'>{t('projects.report.selectProject')}</Text>}
            </Stack>
        </Content>
    )
}

export default DashboardProject
